/**
 * @param registrations - Registered event occurrences ids []
 * @param occurrenceId - Event occurrence id
 */
function checkRegistration(registrations, occurrenceId) {
  return registrations.includes(occurrenceId);
}

/**
 * Converts appointment and occurrence to a FullCalendar Event instance.
 * @param appt - Unwrapped appointment model data.
 * @param occur - Appointment occurrence model data.
 * @returns {{extendedProps: {appointment_id, model}, start, end, id, title}}
 * Converted FullCalendar Event instance.
 */
export function eventForAppointmentOccurrence(appt, occur) {
  const props = {
    id: occur.id,
    title: appt.name,
    start: occur.start_at,
    end: occur.end_at,
    extendedProps: {
      kind: 'appointment',
      model: occur,
      appointment_id: appt.id,
    },
  };
  if (occur.cancelled) {
    // colorText property isn't working so using a class as a workaround
    props.className = 'fc-event-cancelled';
    props.color = '#6f6f6f';
  }
  return props;
}

/**
 * Converts event and occurrence to a FullCalendar Event instance.
 * @param event - Unwrapped event model data.
 * @param occur - Event occurrence model data.
 * @param registrations - array of registered event occurrences ids
 * @returns {{extendedProps: {event_id, model, eventType, description, isRegistered},
 * start, end, id, title, color}}
 * Converted FullCalendar Event instance.
 */
function eventForEventOccurrence(event, occur, registrations) {
  const isRegistered = checkRegistration(registrations, occur.id);
  const props = {
    id: occur.id,
    title: event.name,
    start: occur.start_at,
    end: occur.end_at,
    color: isRegistered ? 'green' : '#995900',
    extendedProps: {
      kind: 'event',
      eventType: event.event_type,
      description: event.description,
      event_id: event.id,
      model: occur,
      isRegistered,
    },
  };
  if (occur.cancelled) {
    // colorText property isn't working so using a class as a workaround
    props.className = 'fc-event-cancelled';
    props.color = '#6f6f6f';
  }
  return props;
}

/**
 * Converts appointment and events to FullCalendar Event instance(s).
 * @param eventObject - data from server, optionally root-wrapped.
 * @param events - Optional array that receives converted objects.
 * @param registrations - Optional array of registered event occurrences ids
 * @returns {Array} Converted FullCalendar Event instance(s).
 */
export function convertEventsObject(eventObject, events = [], registrations = []) {
  // Unwrap from root, if wrapped. Handle case where data is already unwrapped.
  let a = eventObject; // Default to the object itself
  if (eventObject?.event) {
    a = eventObject.event;
  } else if (eventObject?.appointment) {
    a = eventObject.appointment;
  }

  // Ensure 'a' is defined and has occurrences before proceeding
  if (a && a.occurrences_attributes) {
    a.occurrences_attributes.forEach((o) => {
      // Determine if it's an event or appointment based on original structure or properties of 'a'
      const isEvent = eventObject.event || a.event_type;
      if (isEvent) {
        events.push(eventForEventOccurrence(a, o, registrations));
      } else {
        // Assume appointment if not event
        events.push(eventForAppointmentOccurrence(a, o));
      }
    });
  }

  return events;
}

/**
 * Converts appointments and events to FullCalendar Event instance(s).
 * @param eventObject - data from server.
 * @param events - Optional array that receives converted objects.
 * @param registrations - Optional array of registered event occurrences ids
 * @returns {Array} Converted FullCalendar Event instance(s).
 */
export function convertEventsObjects(eventObject, events = [], registrations = []) {
  eventObject.forEach(w => convertEventsObject(w, events, registrations));

  return events;
}
